import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

// Keep these ready-made snippets aligned with the Health Detail probes so
// operators can "show me how you got that number" themselves.
const SNIPPETS = [
  { label: 'Top 20 tables by disk size',
    sql: `SELECT database, table,
       formatReadableSize(sum(bytes_on_disk)) AS size,
       sum(rows) AS rows,
       count() AS parts
FROM system.parts
WHERE active = 1
  AND database NOT IN ('system','INFORMATION_SCHEMA','information_schema')
GROUP BY database, table
ORDER BY sum(bytes_on_disk) DESC
LIMIT 20` },
  { label: 'Disk headroom on this node',
    sql: `SELECT name, path,
       formatReadableSize(free_space)      AS free,
       formatReadableSize(total_space)     AS total,
       formatReadableSize(keep_free_space) AS reserved
FROM system.disks` },
  { label: 'Active merges (in-flight)',
    sql: `SELECT database, table, round(elapsed, 1) AS secs,
       round(progress * 100, 1) AS pct,
       num_parts,
       formatReadableSize(total_size_bytes_compressed) AS size
FROM system.merges
ORDER BY elapsed DESC` },
  { label: 'Slowest 20 SELECTs (last hour)',
    sql: `SELECT user, query_duration_ms AS ms,
       read_rows,
       formatReadableSize(read_bytes) AS bytes_read,
       substring(query, 1, 200) AS query
FROM system.query_log
WHERE event_time >= now() - INTERVAL 1 HOUR
  AND type = 'QueryFinish'
  AND query_kind = 'Select'
  AND is_initial_query = 1
ORDER BY query_duration_ms DESC
LIMIT 20` },
  { label: 'Per-tenant row counts on the logs table',
    sql: `SELECT org_id, count() AS rows
FROM verops.logs
GROUP BY org_id
ORDER BY rows DESC` },
  { label: 'Running queries right now',
    sql: `SELECT query_id, user, elapsed,
       formatReadableSize(memory_usage) AS mem,
       substring(query, 1, 200) AS query
FROM system.processes
ORDER BY elapsed DESC` },
];

/**
 * Operator SQL console. Runs read-only SELECT / SHOW / DESCRIBE /
 * EXPLAIN against a cluster's leader via the backend's safety-wrapped
 * execute endpoint. Every run is audited.
 */
const ClickHouseSqlConsoleDialog = ({ open, clusters, onClose, defaultClusterId }) => {
  const [clusterId, setClusterId] = useState('');
  const [sql, setSql] = useState('SELECT 1 + 1 AS result');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setError(null);
      if (defaultClusterId) setClusterId(String(defaultClusterId));
      else if (!clusterId && clusters && clusters.length > 0) setClusterId(String(clusters[0].id));
    }
  }, [open, defaultClusterId, clusters, clusterId]);

  const run = useCallback(async () => {
    if (!clusterId || !sql || !sql.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await billingService.clusterConsoleQuery(clusterId, sql);
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Query failed');
    } finally {
      setRunning(false);
    }
  }, [clusterId, sql]);

  // Ctrl/Cmd+Enter to run
  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      run();
    }
  };

  const columns = result?.columns || [];
  const rows = result?.rows || [];

  const copyCell = (v) => {
    try {
      navigator.clipboard.writeText(typeof v === 'object' ? JSON.stringify(v) : String(v));
      setCopied(String(v).slice(0, 40));
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const downloadCsv = () => {
    if (!columns.length || !rows.length) return;
    const esc = (v) => {
      if (v == null) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return /[,"\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [columns.join(',')];
    rows.forEach(r => lines.push(columns.map(c => esc(r[c])).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `console-result-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          SQL console (read-only)
          <HelpIcon docKey="sql_console" />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Ctrl/Cmd + Enter to run
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Cluster</Typography>
            <Select size="small" value={clusterId}
                    onChange={(e) => setClusterId(e.target.value)}
                    sx={{ display: 'block', minWidth: 200 }}>
              {(clusters || []).map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name} <Chip size="small" sx={{ ml: 1 }} label={c.status} />
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Snippets</Typography>
            <Select size="small" value=""
                    displayEmpty
                    onChange={(e) => { if (e.target.value) setSql(e.target.value); }}
                    sx={{ display: 'block', minWidth: 280 }}>
              <MenuItem value=""><em>Choose a preset…</em></MenuItem>
              {SNIPPETS.map((s) => (
                <MenuItem key={s.label} value={s.sql}>{s.label}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" color="primary"
                  startIcon={running ? <CircularProgress size={14} color="inherit" /> : <RunIcon />}
                  onClick={run} disabled={running || !sql.trim() || !clusterId}>
            {running ? 'Running…' : 'Run'}
          </Button>
        </Stack>

        <TextField
          multiline
          minRows={6}
          maxRows={16}
          fullWidth
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="SELECT 1 + 1"
          sx={{
            fontFamily: 'ui-monospace, Menlo, monospace',
            '& textarea': { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, lineHeight: 1.5 },
          }}
        />

        {running && <LinearProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {result && !error && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" label={`${result.rowCount} rows`} />
              <Chip size="small" label={`${result.elapsedMs} ms`} />
              <Chip size="small" label={`leader: ${result.leaderNode}`} variant="outlined" />
              {result.truncated && (
                <Tooltip title="Result was capped at max_result_rows — add a tighter WHERE or explicit LIMIT for complete results.">
                  <Chip size="small" color="warning" label="truncated" />
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              {copied && <Typography variant="caption" color="success.main">Copied: {copied}</Typography>}
              {rows.length > 0 && <Button size="small" onClick={downloadCsv}>Download CSV</Button>}
            </Stack>

            {rows.length === 0 ? (
              <Alert severity="info">Query ran OK and returned 0 rows.</Alert>
            ) : (
              <Box sx={{ overflow: 'auto', maxHeight: 480, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map(c => (
                        <TableCell key={c} sx={{
                          fontWeight: 600, fontSize: 12,
                          background: 'background.paper',
                        }}>{c}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r, i) => (
                      <TableRow key={i} hover>
                        {columns.map(c => {
                          const v = r[c];
                          const str = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
                          return (
                            <TableCell key={c} sx={{
                              fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
                              maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              <Tooltip title={str} placement="top-start">
                                <span onClick={() => copyCell(str)} style={{ cursor: 'pointer' }}>
                                  {str}
                                </span>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClickHouseSqlConsoleDialog;
